"use client";
import { AssetTypes, TFullMarketInfo } from "@zebraex/server/export";
import { createContext, useContext, useMemo, useRef, type ReactNode } from "react";
import { useStore } from "zustand";
import { create, findInverseMarket, type TOrderForm } from "./order-form-store";
import { useShallow } from "zustand/shallow";

type OrderFormStoreApi = ReturnType<typeof create>;

const OrderFormStoreContext = createContext<OrderFormStoreApi | undefined>(undefined);

function OrderFormStoreProvider({ children }: { children: ReactNode }) {
  const ref = useRef<OrderFormStoreApi>(undefined);

  if (!ref.current) {
    ref.current = create();
  }

  return <OrderFormStoreContext.Provider value={ref.current}>{children}</OrderFormStoreContext.Provider>;
}

function useOrderForm<T>(selector: (store: TOrderForm) => T): T {
  const store = useContext(OrderFormStoreContext);

  if (!store) {
    throw new Error("useFormStore must be used within a FormStoreProvider");
  }

  return useStore(store, useShallow(selector));
}

function useInverseMarket() {
  const marketsList = useOrderForm((state) => state.marketsList);
  const assetFrom = useOrderForm((state) => state.selection.assetFrom.data);
  const assetTo = useOrderForm((state) => state.selection.assetTo.data);

  const inverseMarket = useMemo(() => {
    return findInverseMarket({
      marketsList,
      assetFrom,
      assetTo,
    });
  }, [marketsList, assetFrom, assetTo]);

  return inverseMarket;
}

type TAssetVariant = "assetFrom" | "assetTo";
type TSelectableAsset = AssetTypes.TAssetSelect & { canSelect: boolean };

function useAvailableAssets(variant: TAssetVariant) {
  const marketsList = useOrderForm((state) => state.marketsList);
  const selectedMarket = useOrderForm((state) => state.selection.market);
  const selectedAssetFrom = useOrderForm((state) => state.selection.assetFrom?.data);
  const selectedAssetTo = useOrderForm((state) => state.selection.assetTo?.data);

  return useMemo(() => {
    const assetsFromMap = new Map<string, AssetTypes.TAssetSelect & { canSelect: boolean }>();
    const assetsToMap = new Map<string, AssetTypes.TAssetSelect & { canSelect: boolean }>();

    marketsList.forEach((market: TFullMarketInfo) => {
      assetsFromMap.set(market.asset_from_data.id, { ...market.asset_from_data, canSelect: true });
      assetsToMap.set(market.asset_to_data.id, { ...market.asset_to_data, canSelect: true });
    });

    const assetsFrom = Array.from(assetsFromMap.values());
    const assetsTo = Array.from(assetsToMap.values());

    const isMarketSelected = selectedMarket;

    if (!selectedAssetFrom && !selectedAssetTo) {
      return variant === "assetFrom" ? assetsFrom : assetsTo;
    }

    if (variant === "assetTo" && selectedAssetFrom) {
      return assetsTo.map((asset) => ({
        ...asset,
        canSelect: marketsList.some((market) => market.asset_to_data.id === asset.id && market.asset_from_data.id === selectedAssetFrom.id),
      }));
    }

    return variant === "assetFrom" ? assetsFrom : assetsTo;
  }, [marketsList, selectedAssetFrom, selectedAssetTo, variant]);
}

export { OrderFormStoreProvider, useAvailableAssets, useInverseMarket, useOrderForm, type TAssetVariant, type TSelectableAsset };
